import React, { useCallback, useState } from "react";
import { observer } from "mobx-react";
import {
    BuildOutlined,
    Close,
    ContentCopyRounded,
    ThumbDownOutlined,
    ThumbUpOutlined,
} from "@mui/icons-material";
import { alpha, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxButton from "components/UI/AcxButton";
import { useCurrentChatStore } from "components/KBo/Stores/useCurrentChat";
import { FeedbackType } from "components/Signals/BaseObjects/ChartCard/ChartFeatureStores/AISummaryStore";
import type { FunctionCall } from "components/Admin/Organizations/OrganizationDetail/OrganizationChatReview/types";
import FunctionCallsModal from "components/Admin/Organizations/OrganizationDetail/OrganizationChatReview/FunctionCallsModal";
import { useStore } from "utils/useStore";
import { AuthStore } from "stores/AuthStore";
import { ViewDebugAction } from "../Actions/ViewDebugAction";
import { emptyGUID } from "components/Admin/Organizations/OrganizationDetail/OrganizationModules/OrganizationModuleDetails/OrganizationModuleQuestionDependencies";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";

interface MessageGroupActionsProps {
    onCopy?(): void;
    functionCalls?: FunctionCall[];
    hasDebugAction?: boolean;
    debugActionArgs?: unknown;
}

export const MessageGroupActions = observer(
    (props: MessageGroupActionsProps) => {
        const currentChatStore = useCurrentChatStore();
        const authStore = useStore(AuthStore);
        const messageStore = useStore(MessageStore);
        const theme = useTheme();

        const messageGroup = currentChatStore?.chat.recentAgentMessageGroup;
        const feedbackType =
            messageGroup?.userFeedbackType === "Like"
                ? FeedbackType.Like
                : messageGroup?.userFeedbackType === "Dislike"
                  ? FeedbackType.Dislike
                  : undefined;
        const [showFeedbackInput, setShowFeedbackInput] = useState(false);
        const [feedbackText, setFeedbackText] = useState(
            messageGroup?.userFeedback ?? "",
        );
        const [isFeedbackTextSubmitted, setIsFeedbackTextSubmitted] =
            useState(!!messageGroup?.userFeedback);

        const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
        const [functionCallsModalOpen, setFunctionCallsModalOpen] =
            useState(false);

        const isUltra = authStore.isUserUltra();
        const hasFunctionCalls =
            isUltra &&
            props.functionCalls !== undefined &&
            props.functionCalls.length > 0;

        const isLiked = feedbackType === FeedbackType.Like;
        const isDisliked = feedbackType === FeedbackType.Dislike;
        const hasPersistedChat =
            !!currentChatStore?.chat.id &&
            currentChatStore.chat.id !== emptyGUID;

        const provideFeedback = useCallback(
            async (feedbackType?: FeedbackType, feedback?: string) => {
                if (!currentChatStore || !messageGroup) return false;
                try {
                    setIsFeedbackLoading(true);
                    await currentChatStore?.acxAgentService.provideFeedback(
                        currentChatStore.chat.id,
                        feedbackType ?? null,
                        feedback ?? null,
                    );
                    messageGroup.setFeedback(
                        feedbackType === undefined
                            ? undefined
                            : FeedbackType[feedbackType],
                        feedback,
                    );
                    return true;
                } catch (e: unknown) {
                    messageStore.logError(
                        "Failed to save feedback. Please try again.",
                    );
                    return false;
                } finally {
                    setIsFeedbackLoading(false);
                }
            },
            [currentChatStore, messageGroup, messageStore],
        );

        const getThumbColor = useCallback(
            (active: boolean) =>
                active ? theme.palette.primary.main : theme.palette.gray[300],
            [theme],
        );
        const getThumbHoverColor = useCallback(
            (active: boolean) =>
                active
                    ? alpha(theme.palette.primary.main, 0.9)
                    : theme.palette.gray[400],
            [theme],
        );
        const actionIconSx = {
            fontSize: 16,
            cursor: "pointer",
            color: theme.palette.gray[300],
            ":hover": {
                color: theme.palette.gray[400],
            },
        };

        async function handleThumbClick(thumb: FeedbackType) {
            if (isFeedbackLoading) return;
            const newFeedback = feedbackType !== thumb ? thumb : undefined;
            if (!(await provideFeedback(newFeedback))) return;
            setShowFeedbackInput(newFeedback !== undefined);
            setFeedbackText("");
            setIsFeedbackTextSubmitted(false);
        }

        return (
            <Grid
                container
                item
                direction="column"
                rowGap={1 / 2}
                sx={{ paddingLeft: "36px" }}
            >
                <Grid
                    container
                    item
                    columnGap={1}
                    alignSelf="flex-end"
                    sx={{ width: "100%" }}
                >
                    {hasPersistedChat && (
                        <React.Fragment>
                            <Grid item>
                                <ContentCopyRounded
                                    sx={{
                                        fontSize: 16,
                                        cursor: "pointer",
                                        color: theme.palette.gray[300],
                                        ":hover": {
                                            color: theme.palette.gray[400],
                                        },
                                    }}
                                    onClick={() => props.onCopy?.()}
                                    id="kbo-copy"
                                />
                            </Grid>
                            <Grid item>
                                <ThumbUpOutlined
                                    sx={{
                                        fontSize: 16,
                                        color: getThumbColor(isLiked),
                                        cursor: "pointer",
                                        ":hover": {
                                            color: getThumbHoverColor(isLiked),
                                        },
                                    }}
                                    onClick={() =>
                                        handleThumbClick(FeedbackType.Like)
                                    }
                                    id="kbo-thumbs-up"
                                />
                            </Grid>
                            <Grid item>
                                <ThumbDownOutlined
                                    sx={{
                                        fontSize: 16,
                                        color: getThumbColor(isDisliked),
                                        cursor: "pointer",
                                        ":hover": {
                                            color: getThumbHoverColor(
                                                isDisliked,
                                            ),
                                        },
                                    }}
                                    onClick={() =>
                                        handleThumbClick(FeedbackType.Dislike)
                                    }
                                    id="kbo-thumbs-down"
                                />
                            </Grid>
                            {hasFunctionCalls && (
                                <Grid item>
                                    <Tooltip title="View Tool Calls">
                                        <BuildOutlined
                                            sx={actionIconSx}
                                            onClick={() =>
                                                setFunctionCallsModalOpen(true)
                                            }
                                            id="kbo-tool-calls"
                                        />
                                    </Tooltip>
                                </Grid>
                            )}
                            {props.hasDebugAction && (
                                <Grid item>
                                    <ViewDebugAction
                                        args={props.debugActionArgs}
                                        trigger="icon"
                                        iconSx={actionIconSx}
                                    />
                                </Grid>
                            )}
                        </React.Fragment>
                    )}
                </Grid>
                {showFeedbackInput && (
                    <Grid
                        container
                        item
                        direction="column"
                        rowGap={1}
                        sx={(theme) => ({
                            borderRadius: 1,
                            paddingInline: theme.spacing(2),
                            paddingBlock: theme.spacing(1),
                            backgroundColor: theme.palette.primary[50],
                            color: theme.palette.gray[600],
                        })}
                    >
                        <Grid
                            container
                            item
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Grid item>
                                <Typography
                                    sx={{
                                        color: "#52525B",
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        lineHeight: "20px",
                                    }}
                                >
                                    Why was this a {isLiked ? "good" : "bad"}{" "}
                                    response?
                                </Typography>
                            </Grid>
                            <Grid item sx={{ height: "16px" }}>
                                <Close
                                    sx={(theme) => ({
                                        fontSize: "16px",
                                        cursor: "pointer",
                                        color: theme.palette.gray[400],
                                    })}
                                    onClick={() => setShowFeedbackInput(false)}
                                />
                            </Grid>
                        </Grid>
                        <Grid container item direction="column" width="100%">
                            <Grid item>
                                <AcxMainTextField
                                    id="agent-message-feedback"
                                    textContainerSx={{ margin: 0 }}
                                    value={feedbackText}
                                    onChange={(event) =>
                                        setFeedbackText(event.target.value)
                                    }
                                    placeholderText="Enter feedback..."
                                    autoComplete="off"
                                    isDisabled={isFeedbackTextSubmitted}
                                    multiline
                                />
                            </Grid>
                            {isFeedbackTextSubmitted && (
                                <Grid item>
                                    <Typography
                                        sx={{
                                            color: "#52525B",
                                            fontSize: "11px",
                                            fontWeight: 500,
                                            lineHeight: "20px",
                                            fontStyle: "italic",
                                        }}
                                    >
                                        Feedback recorded
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                        <Grid item alignSelf="flex-end">
                            <AcxButton
                                fullWidth={false}
                                disabled={
                                    !feedbackText || isFeedbackTextSubmitted
                                }
                                loading={isFeedbackLoading}
                                sx={(theme) => ({
                                    borderRadius: theme.shape.borderRadius,
                                    paddingInline: theme.spacing(1),
                                    paddingBlock: theme.spacing(1 / 4),
                                    border: "1px solid",
                                    fontWeight: 600,
                                    lineHeight: "20px",
                                    fontSize: "12px",
                                    height: "24px",
                                    color: theme.palette.gray[700],
                                    backgroundColor: "transparent",
                                    borderColor: theme.palette.gray[700],
                                    ":hover": {
                                        opacity: "75%",
                                        backgroundColor: "transparent",
                                    },
                                    ":disabled": {
                                        color: theme.palette.gray[400],
                                        backgroundColor: "transparent",
                                        borderColor: theme.palette.gray[400],
                                    },
                                })}
                                onClick={async () => {
                                    const saved = await provideFeedback(
                                        feedbackType,
                                        feedbackText,
                                    );
                                    if (saved) setIsFeedbackTextSubmitted(true);
                                }}
                            >
                                Submit
                            </AcxButton>
                        </Grid>
                    </Grid>
                )}
                {hasFunctionCalls && (
                    <FunctionCallsModal
                        open={functionCallsModalOpen}
                        onClose={() => setFunctionCallsModalOpen(false)}
                        functionCalls={props.functionCalls}
                    />
                )}
            </Grid>
        );
    },
);
